import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Handshake,
  Refresh,
  Add,
  TrendingUp,
  AttachMoney,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import {
  partnershipsService,
  Partnership,
  PartnershipFilters,
  PartnershipListResponse,
} from '@/services/partnershipsService';
import PartnershipCard from '@/components/partnerships/PartnershipCard';
import PartnershipFiltersComponent from '@/components/partnerships/PartnershipFilters';
import PartnershipDetailModal from '@/components/partnerships/PartnershipDetailModal';
import ConfirmDialog from '@/components/common/ConfirmDialog';

const PartnershipsListPage: React.FC = () => {
  // State
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PartnershipListResponse | null>(null);
  const [filters, setFilters] = useState<PartnershipFilters>({
    page: 1,
    limit: 12,
    isActive: 'all',
    type: 'all',
    sortBy: 'activatedAt',
    sortOrder: 'desc',
  });

  // Modal states
  const [selectedPartnership, setSelectedPartnership] = useState<Partnership | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    severity: 'warning' as 'warning' | 'error' | 'info' | 'question',
  });

  // Load partnerships data
  const loadPartnerships = async () => {
    setLoading(true);
    try {
      const response = await partnershipsService.getPartnerships(filters);
      if (response.success && response.data) {
        setData(response.data);
      } else {
        // Set empty data state instead of showing error
        setData({
          partnerships: [],
          pagination: { total: 0, page: 1, limit: filters.limit || 12, totalPages: 0 },
          summary: { totalActive: 0, totalInactive: 0, totalRevenue: 0, totalCommissions: 0 },
        });
      }
    } catch (error) {
      // Set empty data state for any errors
      setData({
        partnerships: [],
        pagination: { total: 0, page: 1, limit: filters.limit || 12, totalPages: 0 },
        summary: { totalActive: 0, totalInactive: 0, totalRevenue: 0, totalCommissions: 0 },
      });
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount and when filters change
  useEffect(() => {
    loadPartnerships();
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (newFilters: PartnershipFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      limit: filters.limit,
      isActive: 'all',
      type: 'all',
      sortBy: 'activatedAt',
      sortOrder: 'desc',
    });
  };

  // Pagination
  const handleChangePage = (_event: unknown, newPage: number) => {
    setFilters({ ...filters, page: newPage + 1 });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({
      ...filters,
      limit: parseInt(event.target.value, 10),
      page: 1,
    });
  };

  // View partnership details
  const handleViewPartnership = (partnership: Partnership) => {
    setSelectedPartnership(partnership);
    setDetailModalOpen(true);
  };

  // Reactivate partnership
  const handleReactivatePartnership = async (partnership: Partnership) => {
    setActionLoading(true);
    try {
      const response = await partnershipsService.reactivatePartnership(partnership.id);
      if (response.success) {
        toast.success(response.message || 'Partnership reactivated successfully');
        setDetailModalOpen(false);
        await loadPartnerships();
      } else {
        toast.error(response.error || 'Failed to reactivate partnership');
      }
    } catch (error) {
      toast.error('Failed to reactivate partnership');
    } finally {
      setActionLoading(false);
    }
  };

  // Suspend partnership
  const handleSuspendPartnership = async (partnership: Partnership) => {
    setConfirmDialog({
      open: true,
      title: 'Suspend Partnership',
      message: `Are you sure you want to suspend ${partnership.companyName}? The partnership can be reactivated later.`,
      severity: 'warning',
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const response = await partnershipsService.suspendPartnership(partnership.id);
          if (response.success) {
            toast.success(response.message || 'Partnership suspended successfully');
            setDetailModalOpen(false);
            await loadPartnerships();
          } else {
            toast.error(response.error || 'Failed to suspend partnership');
          }
        } catch (error) {
          toast.error('Failed to suspend partnership');
        } finally {
          setActionLoading(false);
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }
      },
    });
  };

  // Delete partnership
  const handleDeactivatePartnership = async (partnership: Partnership) => {
    setConfirmDialog({
      open: true,
      title: 'Permanently Delete Partnership',
      message: `Are you sure you want to permanently delete ${partnership.companyName}? This action cannot be undone and will remove all partnership data.`,
      severity: 'error',
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const response = await partnershipsService.deletePartnership(partnership.id);
          if (response.success) {
            toast.success(response.message || 'Partnership deleted successfully');
            setDetailModalOpen(false);
            await loadPartnerships();
          } else {
            // Handle specific errors (e.g., has bookings or commissions)
            const errorMessage = response.error || '';
            if (errorMessage.includes('booking') || errorMessage.includes('HAS_BOOKINGS')) {
              toast.error(
                `Cannot delete ${partnership.companyName} - partnership has existing bookings. Suspend the partnership instead.`,
                { duration: 6000 }
              );
            } else if (errorMessage.includes('commission') || errorMessage.includes('HAS_COMMISSIONS')) {
              toast.error(
                `Cannot delete ${partnership.companyName} - partnership has commission records. Suspend the partnership instead.`,
                { duration: 6000 }
              );
            } else {
              toast.error(response.error || 'Failed to delete partnership');
            }
          }
        } catch (error) {
          toast.error('Failed to delete partnership');
        } finally {
          setActionLoading(false);
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }
      },
    });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Handshake sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Partnership Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and monitor all partnership accounts
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadPartnerships}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => toast.info('Please activate partnerships from Corporate Accounts')}
          >
            Activate Partnership
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      {data?.summary && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <TrendingUp color="success" />
                  <Typography variant="caption" color="text.secondary">
                    Active Partnerships
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {data.summary.totalActive.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <TrendingUp color="default" />
                  <Typography variant="caption" color="text.secondary">
                    Inactive
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {data.summary.totalInactive.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <AttachMoney color="success" />
                  <Typography variant="caption" color="text.secondary">
                    Total Revenue (YTD)
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {formatCurrency(data.summary.totalRevenue)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <AttachMoney color="warning" />
                  <Typography variant="caption" color="text.secondary">
                    Total Commission (YTD)
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {formatCurrency(data.summary.totalCommissions)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <PartnershipFiltersComponent
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" py={8}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty State */}
      {!loading && data && data.partnerships.length === 0 && (
        <Paper
          variant="outlined"
          sx={{
            mt: 3,
            py: 8,
            px: 4,
            textAlign: 'center',
            bgcolor: 'background.default',
          }}
        >
          <Handshake
            sx={{
              fontSize: 80,
              color: 'text.disabled',
              mb: 2,
            }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Partnerships Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
            {data.summary.totalActive === 0 && data.summary.totalInactive === 0
              ? 'Get started by activating your first partnership from the Corporate Accounts page.'
              : 'No partnerships match your current filters. Try adjusting your search criteria.'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleClearFilters}
            sx={{ mr: 2 }}
          >
            Clear Filters
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => toast.info('Please activate partnerships from Corporate Accounts')}
          >
            Activate Partnership
          </Button>
        </Paper>
      )}

      {/* Partnership Cards Grid */}
      {!loading && data && data.partnerships.length > 0 && (
        <>
          <Grid container spacing={3}>
            {data.partnerships.map((partnership) => (
              <Grid item xs={12} sm={6} md={4} key={partnership.id}>
                <PartnershipCard
                  partnership={partnership}
                  onView={handleViewPartnership}
                  onActivate={handleReactivatePartnership}
                  onSuspend={handleSuspendPartnership}
                  onDelete={handleDeactivatePartnership}
                />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          <Paper variant="outlined" sx={{ mt: 3 }}>
            <TablePagination
              component="div"
              count={data.pagination.total}
              page={data.pagination.page - 1}
              onPageChange={handleChangePage}
              rowsPerPage={data.pagination.limit}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[6, 12, 24, 48]}
            />
          </Paper>
        </>
      )}

      {/* Partnership Detail Modal */}
      {selectedPartnership && (
        <PartnershipDetailModal
          open={detailModalOpen}
          partnershipId={selectedPartnership.id}
          onClose={() => setDetailModalOpen(false)}
          onReactivate={() => handleReactivatePartnership(selectedPartnership)}
          onDeactivate={() => handleDeactivatePartnership(selectedPartnership)}
        />
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        severity={confirmDialog.severity}
        confirmText={confirmDialog.severity === 'error' ? 'Delete' : 'Confirm'}
        cancelText="Cancel"
        loading={actionLoading}
      />
    </Container>
  );
};

export default PartnershipsListPage;
